/**
 * Renders the licensing banner / nag / hard-expired-gate based on the
 * current license state. Consumers wrap their main pane like:
 *
 *   <LicenseBanner license={license} onUpgrade={...}>
 *     <TaskPane ... />
 *   </LicenseBanner>
 *
 * When status === 'expired', children are replaced by a full-pane
 * upgrade prompt (hard disable). In all other states, children render
 * and the banner appears above them.
 */
import { ReactNode } from 'react';
import { MessageBar, MessageBarBody, MessageBarTitle, MessageBarActions, Button, Text } from '@fluentui/react-components';
import type { License } from '../hooks/useLicense';
import { credsAgeBucket } from '../services/cw-api';

interface Props {
  license: License | null;
  children: ReactNode;
  onUpgrade?: () => void;
}

function daysUntil(iso: string | null): number | null {
  if (!iso) return null;
  const ms = new Date(iso).getTime() - Date.now();
  if (!Number.isFinite(ms)) return null;
  return Math.max(0, Math.ceil(ms / (24 * 60 * 60 * 1000)));
}

function defaultUpgrade() {
  window.open('https://bluprint-ai.com/pricing', '_blank', 'noopener');
}

export default function LicenseBanner({ license, children, onUpgrade }: Props) {
  const upgrade = onUpgrade || defaultUpgrade;
  const creds = credsAgeBucket();

  // Creds-age banner surfaces ahead of any license-status banner because
  // it's the more actionable warning — an expired key stops the whole
  // app from working regardless of subscription state. Rendered above
  // the license banner when both fire simultaneously.
  const credsBanner = creds.bucket === 'warn' ? (
    <MessageBar intent="warning">
      <MessageBarBody>
        <MessageBarTitle>Rotate your ConnectWise API credentials</MessageBarTitle>
        Your CW credentials are {creds.days} days old. Most teams rotate on a 180-day cadence — update in CW (System → Members → Your Member → API Keys → regenerate) and re-enter them here to avoid service interruption.
      </MessageBarBody>
    </MessageBar>
  ) : creds.bucket === 'expired' ? (
    <MessageBar intent="error">
      <MessageBarBody>
        <MessageBarTitle>ConnectWise API credentials are stale</MessageBarTitle>
        Your CW credentials are {creds.days} days old — if ConnectWise rotation policy has kicked in, they may already be revoked. Regenerate in CW and re-enter them here.
      </MessageBarBody>
    </MessageBar>
  ) : null;

  if (!license) return <>{credsBanner}{children}</>;

  if (license.status === 'expired') {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <Text weight="semibold" size={500} style={{ display: 'block', marginBottom: '0.75rem' }}>
          Your subscription has expired
        </Text>
        <Text size={300} style={{ display: 'block', marginBottom: '1.5rem', maxWidth: '32rem', marginLeft: 'auto', marginRight: 'auto' }}>
          Logging is paused until you reactivate. Your settings and cached data are preserved — reactivate to resume where you left off.
        </Text>
        <Button appearance="primary" onClick={upgrade}>
          Reactivate subscription
        </Button>
      </div>
    );
  }

  let banner: ReactNode = null;
  if (license.status === 'trial') {
    const days = daysUntil(license.expires_at);
    banner = (
      <MessageBar intent="info">
        <MessageBarBody>
          <MessageBarTitle>Trial</MessageBarTitle>
          {days == null
            ? 'You are on a 14-day trial.'
            : days === 0
            ? 'Trial expires today.'
            : `Trial expires in ${days} day${days === 1 ? '' : 's'}.`}
        </MessageBarBody>
        <MessageBarActions>
          <Button appearance="primary" size="small" onClick={upgrade}>Upgrade</Button>
        </MessageBarActions>
      </MessageBar>
    );
  } else if (license.status === 'grace') {
    const days = daysUntil(license.expires_at);
    banner = (
      <MessageBar intent="warning">
        <MessageBarBody>
          <MessageBarTitle>Subscription expired</MessageBarTitle>
          {days == null
            ? 'Reactivate soon to avoid losing access.'
            : `Logging will be disabled in ${days} day${days === 1 ? '' : 's'}.`}
        </MessageBarBody>
        <MessageBarActions>
          <Button appearance="primary" size="small" onClick={upgrade}>Reactivate</Button>
        </MessageBarActions>
      </MessageBar>
    );
  }

  return (
    <>
      {credsBanner}
      {banner}
      {children}
    </>
  );
}
